/**
 * Which queries may be written to localStorage by the TanStack persister.
 *
 * Lives here rather than in index.js so it can be tested as the pure rule it
 * is — importing the bootstrap file drags in the entire app.
 */

// Skip persisting:
//   - anything not SETTLED and SUCCESSFUL (a pending query cannot be
//     serialized — see the note on the predicate itself)
//   - queries that errored (don't hydrate broken state into a fresh tab)
//   - queries with hooks that explicitly opt out via meta.persist = false
//     (auth state, payment flows, anything sensitive or short-lived)
//   - queries whose key references a path-style parameter that no longer
//     resolves on the server. We can't predict the server side, so we
//     use the heuristic "errorUpdatedAt > dataUpdatedAt" — i.e. the
//     last thing that happened to this query was a failure, even if it
//     once succeeded. This catches the case where a vendor / agency /
//     workspace name gets renamed or removed: the old key would
//     otherwise live in localStorage for 24h and produce a 404 storm on
//     every reload via the background revalidation TanStack runs on
//     hydration.
// Everything else is fair game; the persister also handles serialization
// of Map / Set / Date through its default JSON.stringify wrapper.
export function dehydratedQueryShouldPersist(query) {
  // ONLY settled, successful queries. This line is the important one, and it
  // restores the v5 default that overriding shouldDehydrateQuery silently
  // discards: TanStack's own predicate is `status === 'success'`, and a
  // predicate that merely excludes 'error' therefore persists queries that are
  // still PENDING.
  //
  // A pending query cannot survive serialization — its promise does not go into
  // localStorage. On the next load, hydration resumes it, finds nothing, and
  // rejects:
  //
  //   A query that was dehydrated as pending ended up rejecting.
  //   [["cls-loop","al-stats"]]: Cannot read properties of undefined (reading 'error')
  //
  // Measured 2026-09-16: 68 of these in one session on /accounts. It is not
  // specific to that query — the persister writes on a 1s throttle, so ANY
  // query in flight when it fires is captured mid-pending. `cls-loop` wins the
  // race most often only because NavSidebar fires four classification-stat
  // queries on every page in the app.
  if (query.state.status !== 'success') return false;
  if (query.meta?.persist === false) return false;
  const { errorUpdatedAt = 0, dataUpdatedAt = 0 } = query.state;
  if (errorUpdatedAt > dataUpdatedAt) return false;
  return true;
}

